import React, { useEffect, useRef, useCallback, useState } from 'react';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import './VideoPlayerModal.css';
import { API_BASE_URL } from '../../../../API/api';
import { backdropCloseHandlers } from '../../../../utils/backdropClose';

/** Build playback URL: MySQL-backed files use /files/:id/download; virtual Drive rows use project Drive route. */
function resolveVideoPlaybackUrl(file) {
  if (!file) return null;
  const projectId = file.projectId;
  const projectQuery =
    projectId !== undefined && projectId !== null && projectId !== ''
      ? `?project_id=${encodeURIComponent(projectId)}`
      : '';
  const driveId =
    (file.googleDriveId && String(file.googleDriveId).trim()) ||
    (typeof file.id === 'string' && file.id.startsWith('gd_')
      ? file.id.slice(3)
      : null);
  if (file.isGoogleDriveItem && driveId) {
    if (projectId === undefined || projectId === null || projectId === '') {
      console.error('[VideoPlayer] Google Drive item missing projectId');
      return null;
    }
    const enc = encodeURIComponent(driveId);
    return `${API_BASE_URL}/api/projects/${encodeURIComponent(projectId)}/google-drive-files/${enc}/download`;
  }
  return `${API_BASE_URL}/api/files/${file.id}/download${projectQuery}`;
}

const VideoPlayerModal = ({ isOpen, onClose, file }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const bufferCheckIntervalRef = useRef(null);
  const isLoadingRef = useRef(true); // Use ref to track loading state to avoid stale closures
  const monitoringStartedRef = useRef(false);
  const readyEventFiredRef = useRef(false);
  const fallbackTimeoutRef = useRef(null);
  const sixtySecondTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !file || !videoRef.current) return;

    const videoUrlEarly = resolveVideoPlaybackUrl(file);
    if (!videoUrlEarly) {
      console.error('[VideoPlayer] No playback URL for file:', file?.name);
      isLoadingRef.current = false;
      setIsLoading(false);
      return undefined;
    }

    // CRITICAL: Clear any existing interval FIRST to prevent stale intervals
    if (bufferCheckIntervalRef.current) {
      console.log('[VideoPlayer] ⚠️ Clearing stale buffer monitoring interval');
      clearInterval(bufferCheckIntervalRef.current);
      bufferCheckIntervalRef.current = null;
    }
    
    // Clear fallback timeout if exists
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    
    // Clear 120 second timeout if exists
    if (sixtySecondTimeoutRef.current) {
      console.log('[VideoPlayer] ⚠️ Clearing stale 120 second timeout');
      clearTimeout(sixtySecondTimeoutRef.current);
      sixtySecondTimeoutRef.current = null;
    }

    // Reset all tracking refs
    monitoringStartedRef.current = false;
    readyEventFiredRef.current = false;

    // Reset loading state when modal opens - MUST be after clearing interval
    console.log('[VideoPlayer] Loading animation STARTED - modal opened for file:', file.name);
    isLoadingRef.current = true;
    setIsLoading(true);

    const videoUrl = videoUrlEarly;
    console.log('[VideoPlayer] Initializing Plyr player with video URL:', videoUrl, {
      isGoogleDriveItem: !!file.isGoogleDriveItem,
    });

    // Initialize Plyr with vanilla/default settings
    const player = new Plyr(videoRef.current, {
      autoplay: true,
    });

    playerRef.current = player;

    // Set video source
    player.source = {
      type: 'video',
      sources: [
        {
          src: videoUrl,
          type: 'video/mp4',
        },
      ],
    };
    console.log('[VideoPlayer] Video source set, waiting for Plyr ready event...');

    // Real-time buffer monitoring function - ONLY source of truth for loading state
    let checkCount = 0;
    const checkBufferProgress = () => {
      // Only check if we're still in loading state
      if (!isLoadingRef.current) {
        return true; // Already hidden, stop checking
      }

      checkCount++;
      
      // Check if video element exists
      if (!videoRef.current) {
        if (checkCount % 20 === 0) {
          console.log('[VideoPlayer] Buffer check #' + checkCount + ': Video element not found');
        }
        return false;
      }

      // Try multiple ways to find the Plyr container
      let playerContainer = videoRef.current.closest('.plyr');
      
      // If closest doesn't work, try querySelector on document
      if (!playerContainer) {
        playerContainer = document.querySelector('.plyr');
      }
      
      // If still not found, check parent structure
      if (!playerContainer) {
        if (checkCount % 20 === 0) { // Log every 20 checks to avoid spam
          const videoParent = videoRef.current?.parentElement;
          const videoParentClasses = videoParent?.className || 'no parent';
          const videoClasses = videoRef.current?.className || 'no classes';
          console.log('[VideoPlayer] Buffer check #' + checkCount + ': Plyr container not found', {
            videoElementExists: !!videoRef.current,
            videoClasses,
            videoParentClasses,
            videoParentTag: videoParent?.tagName,
            allPlyrElements: document.querySelectorAll('.plyr').length
          });
        }
        return false;
      }

      // Try to find buffer progress element
      let bufferProgress = playerContainer.querySelector('progress.plyr__progress__buffer');
      
      // If not found, try alternative selectors
      if (!bufferProgress) {
        bufferProgress = playerContainer.querySelector('.plyr__progress__buffer');
      }
      
      if (!bufferProgress) {
        if (checkCount % 20 === 0) { // Log every 20 checks to avoid spam
          const allProgressElements = playerContainer.querySelectorAll('progress');
          console.log('[VideoPlayer] Buffer check #' + checkCount + ': Buffer progress element not found', {
            playerContainerFound: !!playerContainer,
            playerContainerClasses: playerContainer.className,
            allProgressElements: allProgressElements.length,
            progressElements: Array.from(allProgressElements).map(el => ({
              classes: el.className,
              value: el.getAttribute('value'),
              tag: el.tagName
            }))
          });
        }
        return false;
      }

      // Get value from attribute first (most reliable)
      const valueAttr = bufferProgress.getAttribute('value');
      const valueProp = bufferProgress.value;
      
      // Parse the value - handle both string and number
      let bufferValue = 0;
      if (valueAttr !== null && valueAttr !== undefined && valueAttr !== '') {
        const parsed = parseFloat(valueAttr);
        if (!isNaN(parsed)) {
          bufferValue = parsed;
        }
      } else if (valueProp !== null && valueProp !== undefined) {
        const parsed = parseFloat(valueProp);
        if (!isNaN(parsed)) {
          bufferValue = parsed;
        }
      }

      // Log every check (but throttle to avoid console spam)
      if (checkCount % 10 === 0 || bufferValue > 0) {
        console.log('[VideoPlayer] Buffer check #' + checkCount + ':', {
          bufferValue,
          valueAttr,
          valueProp,
          isLoading: isLoadingRef.current
        });
      }

      // ONLY hide loading if buffer value is actually > 0 (strict check)
      if (bufferValue > 0) {
        console.log('[VideoPlayer] ✅ Buffer progress > 0 DETECTED! Hiding loading animation', {
          checkNumber: checkCount,
          bufferValue,
          valueAttr,
          valueProp,
          timestamp: new Date().toISOString()
        });
        isLoadingRef.current = false;
        setIsLoading(false);
        console.log('[VideoPlayer] Loading animation HIDDEN');
        
        // Clear the 120 second timeout since we don't need it anymore
        if (sixtySecondTimeoutRef.current) {
          console.log('[VideoPlayer] Clearing 120 second timeout - buffer detected');
          clearTimeout(sixtySecondTimeoutRef.current);
          sixtySecondTimeoutRef.current = null;
        }
        
        return true;
      }
      
      return false;
    };

    // Wait a bit for Plyr to initialize DOM, then start monitoring
    const startMonitoring = () => {
      // Double-check: ensure we're not already monitoring and that this is still the current video
      if (bufferCheckIntervalRef.current !== null) {
        console.log('[VideoPlayer] ⚠️ Monitoring already started, skipping duplicate start');
        return;
      }
      
      // Additional guard: check if we're still supposed to be loading
      if (!isLoadingRef.current) {
        console.log('[VideoPlayer] ⚠️ Loading already hidden, not starting monitoring');
        return;
      }
      
      if (monitoringStartedRef.current) {
        console.log('[VideoPlayer] ⚠️ Monitoring start function already called, skipping');
        return;
      }
      
      monitoringStartedRef.current = true;
      console.log('[VideoPlayer] 🚀 Starting buffer monitoring (checking every 50ms)');
      
      // Reset check count for this video
      checkCount = 0;
      
      // Start monitoring buffer immediately - check every 50ms for real-time response
      bufferCheckIntervalRef.current = setInterval(() => {
        // Safety check: ensure this interval is still valid
        if (!videoRef.current || !isOpen) {
          console.log('[VideoPlayer] ⚠️ Video element or modal closed, stopping monitoring');
          if (bufferCheckIntervalRef.current) {
            clearInterval(bufferCheckIntervalRef.current);
            bufferCheckIntervalRef.current = null;
          }
          return;
        }
        
        if (checkBufferProgress()) {
          // Buffer > 0, stop monitoring
          console.log('[VideoPlayer] Buffer monitoring STOPPED - buffer > 0 confirmed');
          if (bufferCheckIntervalRef.current) {
            clearInterval(bufferCheckIntervalRef.current);
            bufferCheckIntervalRef.current = null;
          }
          // Clear 120 second timeout since buffer is detected
          if (sixtySecondTimeoutRef.current) {
            clearTimeout(sixtySecondTimeoutRef.current);
            sixtySecondTimeoutRef.current = null;
          }
        }
      }, 50);
    };

    // Wait for Plyr to be ready before starting monitoring
    player.on('ready', () => {
      // Prevent duplicate ready event handlers
      if (readyEventFiredRef.current) {
        console.log('[VideoPlayer] ⚠️ Plyr ready event fired again (duplicate), ignoring');
        return;
      }
      readyEventFiredRef.current = true;
      console.log('[VideoPlayer] Plyr ready event fired');
      
      // Debug: Check DOM structure after ready
      setTimeout(() => {
        const videoEl = videoRef.current;
        const plyrContainer = videoEl?.closest('.plyr') || document.querySelector('.plyr');
        console.log('[VideoPlayer] DOM structure after ready:', {
          videoElement: !!videoEl,
          videoParent: videoEl?.parentElement?.className,
          plyrContainer: !!plyrContainer,
          plyrContainerClasses: plyrContainer?.className,
          bufferProgress: plyrContainer?.querySelector('progress.plyr__progress__buffer') ? 'found' : 'not found',
          currentFileId: file.id
        });
      }, 50);
      
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        console.log('[VideoPlayer] Starting monitoring after 100ms delay (from ready event)');
        startMonitoring();
      }, 100);
    });

    // Also start monitoring after a short delay as fallback
    fallbackTimeoutRef.current = setTimeout(() => {
      if (bufferCheckIntervalRef.current === null && isLoadingRef.current) {
        console.log('[VideoPlayer] Starting monitoring after 500ms delay (fallback)');
        startMonitoring();
      } else if (!isLoadingRef.current) {
        console.log('[VideoPlayer] Fallback timeout: Loading already hidden, skipping');
      } else {
        console.log('[VideoPlayer] Fallback timeout: Monitoring already started');
      }
    }, 500);

    // Fallback: stop monitoring after 120 seconds
    sixtySecondTimeoutRef.current = setTimeout(() => {
      // Only execute if this timeout is still valid (not cleared)
      if (sixtySecondTimeoutRef.current) {
        console.log('[VideoPlayer] ⏰ 120 second timeout reached - stopping buffer monitoring');
        if (bufferCheckIntervalRef.current) {
          clearInterval(bufferCheckIntervalRef.current);
          bufferCheckIntervalRef.current = null;
        }
        // Only hide if still loading (don't force hide if already hidden)
        if (isLoadingRef.current) {
          console.log('[VideoPlayer] ⚠️ Buffer monitoring timeout - hiding loading as fallback');
          isLoadingRef.current = false;
          setIsLoading(false);
          console.log('[VideoPlayer] Loading animation HIDDEN (timeout fallback)');
        } else {
          console.log('[VideoPlayer] Loading already hidden, no action needed');
        }
        sixtySecondTimeoutRef.current = null;
      }
    }, 120000);

    // Cleanup
    return () => {
      console.log('[VideoPlayer] Cleanup: Destroying player and clearing intervals for file:', file?.name);
      
      // Clear fallback timeout
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
      
      // Clear 120 second timeout
      if (sixtySecondTimeoutRef.current) {
        console.log('[VideoPlayer] Cleanup: Clearing 120 second timeout');
        clearTimeout(sixtySecondTimeoutRef.current);
        sixtySecondTimeoutRef.current = null;
      }
      
      // Clear monitoring interval
      if (bufferCheckIntervalRef.current) {
        console.log('[VideoPlayer] Cleanup: Clearing buffer monitoring interval');
        clearInterval(bufferCheckIntervalRef.current);
        bufferCheckIntervalRef.current = null;
      }
      
      // Destroy player
      if (playerRef.current) {
        console.log('[VideoPlayer] Cleanup: Destroying Plyr player');
        playerRef.current.destroy();
        playerRef.current = null;
      }
      
      // Reset refs
      isLoadingRef.current = true;
      monitoringStartedRef.current = false;
      readyEventFiredRef.current = false;
    };
  }, [isOpen, file]);

  // Handle modal close
  const handleClose = useCallback(() => {
    console.log('[VideoPlayer] Modal close: Clearing all timers and intervals');
    
    // Clear 120 second timeout
    if (sixtySecondTimeoutRef.current) {
      clearTimeout(sixtySecondTimeoutRef.current);
      sixtySecondTimeoutRef.current = null;
    }
    
    // Clear fallback timeout
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    
    // Clear monitoring interval
    if (bufferCheckIntervalRef.current) {
      clearInterval(bufferCheckIntervalRef.current);
      bufferCheckIntervalRef.current = null;
    }
    
    // Destroy player
    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }
    
    onClose();
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  if (!isOpen || !file) return null;

  return (
    <div className="video-player-modal-overlay" {...backdropCloseHandlers(handleClose)}>
      <div className="video-player-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="video-player-modal-header">
          <h3 className="video-player-modal-title">{file.name}</h3>
          <button className="video-player-modal-close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="video-player-modal-body">
          {isLoading && (
            <div className="video-loading-overlay">
              <div className="video-loading-spinner"></div>
              <p className="video-loading-text">Preparing video...</p>
            </div>
          )}
          <div className="plyr-container">
            <video
              ref={videoRef}
              className="plyr-react"
              playsInline
              preload="auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerModal;
